import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CalendarOutlined,
  CheckSquareOutlined,
} from '@ant-design/icons';
import { IconButton, ScopeButton } from '@monorepo/shared-lib';
import {
  Button,
  DatePicker,
  Modal,
  Row,
  Select,
  Skeleton,
  Space,
  Typography,
} from 'antd';
import { ProviderIcon } from 'components/Custom/ProviderIcon';
import { ProviderLogoNav } from 'components/Custom/ProviderLogoNav';
import { FilterSelect } from 'components/Custom/selects';
import { ScopeTabs } from 'components/Custom/tabs';
import { DashboardContainer, StrongTag } from 'components/Custom/spans';
import { ScopeTableFilters } from 'components/Custom/ScopeTableFilters';
import { ScopeTable } from 'components/Custom/table';
import { StyledTooltip } from 'components/Custom/Tooltip';
import { FilterSelectionDrawer } from 'components/MainComponent/FilterSelectionDrawer';
import { EmptyPage } from 'containers/EmptyPage';
import { applyFilters } from 'filter-system';
import moment from 'moment';
import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { formatDisplayDate } from 'i18n/calendarLabels';
import { useCountryLabel } from 'hooks/useCountryLabel';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import routes from 'routes';
import { AccountType } from 'store/api/enums';
import { getReturnFrequencyLabel } from 'i18n/returnFrequencyLabel';
import { turnDrawerOff, turnDrawerOn } from 'store/app/actions';
import { DrawerType } from 'store/app/enums';
import { DrawerData } from 'store/app/types';
import { getAuthUser } from 'store/auth/selectors';
import {
  getOrgLoading,
  getOrgMappedReturns,
  getOrgOrganisations,
} from 'store/organisation/selectors';
import { MappedReturn } from 'store/returns/models/returnData';
import { getReturnLoading } from 'store/returns/selectors';
import { getVatPrepLoading } from 'store/vat/selectors';
import { calculateNextPeriod } from 'utils/calculate-next-period';
import { clearOrganisationDetailsTabStorage } from 'utils/clear-organisation-tab-storage';
import { getEntityOrganisationPath } from 'utils/get-entity-organisation-path';
import {
  Preference,
  PreferenceActions,
  PreferenceSections,
  usePreferences,
} from 'utils/preferences';
import { AvatarSizes, renderAvatar } from 'utils/render-avatar';
import { StatusGetter } from 'utils/status-getter';

const PLANNER_PAGE_SIZE = 'planner-page-size';
const PLANNER_VISIBLE_FILTERS = 'planner-visible-filters';

/** Only these 4 filters are available on Planner (default visible = all 4). No other filters in the drawer. */
export const PLANNER_AVAILABLE_FILTERS = [
  'nextDueDate', // Due date
  'user', // User
  'mappingName', // Tax return
  'period', // Period
];

// --- Planner extended fields (sample data until backend) ---
export type PlannerPriority = 'Low' | 'Medium' | 'High';
export type PlannerComplexity = 'Low' | 'Medium' | 'High';

export type PlannerRowExtension = {
  plannedDate?: string | null; // Commencement date
  priority?: PlannerPriority | null;
  complexity?: PlannerComplexity | null;
  comments?: string | null;
  documentsRequestedDate?: string | null;
  documentsReceivedDate?: string | null;
};

export type PlannerRow = MappedReturn & PlannerRowExtension;

/** Stable key for a planner row (for selection and overrides). */
export function getPlannerRowKey(r: MappedReturn): string {
  return `${r.connectionId ?? ''}-${r.mappingId ?? ''}-${r.key ?? 0}`;
}

/** Simple hash for deterministic sample data from a string. */
function hashStr(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

const PRIORITIES: PlannerPriority[] = ['Low', 'Medium', 'High'];
const COMPLEXITIES: PlannerComplexity[] = ['Low', 'Medium', 'High'];

/** Add sample planner fields for UI/demo until backend stores them. */
function addSamplePlannerData(
  returns: MappedReturn[] | undefined
): PlannerRow[] {
  if (!returns?.length) return [];
  return returns.map((r) => {
    const key = getPlannerRowKey(r);
    const seed = hashStr(key);
    const priority = PRIORITIES[seed % 3];
    const complexity = COMPLEXITIES[(seed >> 2) % 3];
    const nextDue = r.nextDueDate
      ? moment(r.nextDueDate)
      : moment().add(1, 'month');
    const plannedDate = nextDue
      .clone()
      .subtract(7, 'days')
      .format('YYYY-MM-DD');
    const documentsRequestedDate = nextDue
      .clone()
      .subtract(21, 'days')
      .format('YYYY-MM-DD');
    const documentsReceivedDate = nextDue
      .clone()
      .subtract(14, 'days')
      .format('YYYY-MM-DD');
    const comments = seed % 5 === 0 ? 'Sample comment for review' : undefined;
    return {
      ...r,
      plannedDate,
      priority,
      complexity,
      comments: comments ?? null,
      documentsRequestedDate,
      documentsReceivedDate,
    } as PlannerRow;
  });
}

export const PlannerPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { localizeCountryLabel, localizeReturnLabel } = useCountryLabel();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const mappedReturns = useSelector(getOrgMappedReturns);
  const entities = useSelector(getOrgOrganisations);
  const currentUser = useSelector(getAuthUser);
  const orgLoading = useSelector(getOrgLoading);
  const retLoading = useSelector(getReturnLoading);
  const vatLoading = useSelector(getVatPrepLoading);
  const loading = orgLoading || retLoading || vatLoading;
  const preferences = usePreferences();
  const hasEntities = !!entities && entities.length > 0;

  const [sharedFiltersState, setSharedFiltersState] = useState<
    Record<string, any>
  >({});
  const [dataSource, setDataSource] = useState<MappedReturn[] | undefined>(
    mappedReturns
  );
  const [pageSizeSaved, setPageSizeSaved] = useState<number>(20);
  const savedPageSize = preferences(PreferenceActions.GET, {
    name: PLANNER_PAGE_SIZE,
  });
  const savedVisibleFilters = preferences(PreferenceActions.GET, {
    name: PLANNER_VISIBLE_FILTERS,
  });
  const [visibleFilters, setVisibleFilters] = useState<string[]>(() => {
    if (
      savedVisibleFilters &&
      Array.isArray(savedVisibleFilters) &&
      savedVisibleFilters.length > 0
    ) {
      return Array.from(new Set(savedVisibleFilters)).filter((f) =>
        PLANNER_AVAILABLE_FILTERS.includes(f)
      );
    }
    return [...PLANNER_AVAILABLE_FILTERS];
  });
  const [groupBy, setGroupBy] = useState<string>('');
  const canUseManagerView =
    !!currentUser &&
    AccountType[currentUser.accountType] !== AccountType.CLIENT &&
    currentUser.managerViewEnabled === true;
  const [plannerViewType, setPlannerViewType] = useState<'manager' | 'my'>(
    () => (canUseManagerView ? 'manager' : 'my')
  );
  useEffect(() => {
    if (!canUseManagerView && plannerViewType === 'manager') {
      setPlannerViewType('my');
    }
  }, [canUseManagerView, plannerViewType]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [plannerOverrides, setPlannerOverrides] = useState<
    Record<string, Partial<PlannerRowExtension>>
  >({});
  const [planningDetailRecord, setPlanningDetailRecord] =
    useState<PlannerRow | null>(null);
  const [massUpdateMode, setMassUpdateMode] = useState(false);
  const [massUpdateField, setMassUpdateField] = useState<string>('');
  const [massUpdateValue, setMassUpdateValue] = useState<
    string | null | undefined
  >(undefined);

  useEffect(() => {
    const size = typeof savedPageSize === 'number' ? savedPageSize : 20;
    setPageSizeSaved(size);
  }, [savedPageSize]);

  useEffect(() => {
    if (!mappedReturns) return;
    const hasAnyFilters = Object.keys(sharedFiltersState).length > 0;
    if (!hasAnyFilters) {
      setDataSource(mappedReturns);
      return;
    }
    const filtered = applyFilters(mappedReturns, sharedFiltersState, {
      mainLocaleKey: undefined,
      excludeSubmittedCancelledForReturnType: true,
    });
    setDataSource(filtered ?? mappedReturns);
  }, [mappedReturns, sharedFiltersState]);

  const dataSourceRaw = dataSource ?? [];

  // Enrich with sample planner fields and apply user overrides (bulk/single edits)
  const enrichedRows = useMemo(() => {
    const withSample = addSamplePlannerData(dataSourceRaw);
    return withSample.map((row) => {
      const rowKey = getPlannerRowKey(row);
      const over = plannerOverrides[rowKey];
      const merged = over ? ({ ...row, ...over } as PlannerRow) : row;
      (merged as any).plannerRowKey = rowKey;
      return merged;
    });
  }, [dataSourceRaw, plannerOverrides]);

  // Manager view = all; My view = only rows where current user is preparer or reviewer
  const viewFilteredRows = useMemo(() => {
    if (plannerViewType === 'manager') return enrichedRows;
    if (!currentUser?.userUuid) return enrichedRows;
    return enrichedRows.filter(
      (row) =>
        row.preparer?.userUuid === currentUser.userUuid ||
        row.dataLog?.preparerUser?.userUuid === currentUser.userUuid ||
        row.reviewer?.userUuid === currentUser.userUuid ||
        row.dataLog?.reviewerUser?.userUuid === currentUser.userUuid
    );
  }, [enrichedRows, plannerViewType, currentUser?.userUuid]);

  const displayDataSource = useMemo(() => viewFilteredRows, [viewFilteredRows]);

  type GroupByKey =
    | 'mappingName'
    | 'period'
    | 'nextDueDate'
    | 'preparer'
    | 'reviewer'
    | '';

  const GROUP_BY_OPTIONS: { value: GroupByKey; label: string }[] = [
    { value: '', label: t('main.vat-checker-stat-flags-none') },
    { value: 'mappingName', label: t('returns.mapping.tax-return') },
    { value: 'period', label: t('main.period') },
    { value: 'nextDueDate', label: t('main.nextDueDate') },
    { value: 'preparer', label: t('main.preparer') },
    { value: 'reviewer', label: t('main.reviewer') },
  ];

  const formatPreparer = (r: MappedReturn) => {
    const user = r.preparer ?? r.dataLog?.preparerUser;
    if (!user) return '—';
    const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    return name || user.email || '—';
  };
  const formatReviewer = (r: MappedReturn) => {
    const user = r.reviewer ?? r.dataLog?.reviewerUser;
    if (!user) return '—';
    const name = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
    return name || user.email || '—';
  };
  const formatDueDate = (r: MappedReturn) => {
    if (!r.nextDueDate) return '—';
    const m = moment(r.nextDueDate);
    return m.isValid() ? m.format('DD/MM/YYYY') : '—';
  };

  const getGroupValue = (
    record: PlannerRow | (MappedReturn & { children?: MappedReturn[] }),
    key: GroupByKey
  ): string => {
    if ('children' in record && record.children) return '';
    const r = record as PlannerRow;
    switch (key) {
      case 'mappingName':
        return localizeReturnLabel(r.mappingName ?? '—');
      case 'period':
        return getNextPeriodDateRange(r);
      case 'nextDueDate':
        return formatDueDate(r);
      case 'preparer':
        return formatPreparer(r);
      case 'reviewer':
        return formatReviewer(r);
      default:
        return '';
    }
  };

  const buildGroupedDataSource = (
    rows: PlannerRow[],
    groupByKey: GroupByKey
  ): any[] => {
    if (!groupByKey || !rows.length) return rows;
    const map = new Map<string, PlannerRow[]>();
    rows.forEach((row) => {
      const val = getGroupValue(row, groupByKey);
      const list = map.get(val) ?? [];
      list.push(row);
      map.set(val, list);
    });
    const result: any[] = [];
    map.forEach((children, groupValue) => {
      const groupRow: Record<string, any> = {
        key: `group-${groupByKey}-${groupValue}`,
        connectionId: '',
        mappingId: '',
        status: '',
        canDelete: false,
        connectedProperly: false,
        children: children as MappedReturn[],
      };
      if (groupByKey === 'mappingName') groupRow.mappingName = groupValue;
      else if (groupByKey === 'period') groupRow.periodFromDate = groupValue;
      else if (groupByKey === 'nextDueDate') groupRow.nextDueDate = groupValue;
      else if (groupByKey === 'preparer') groupRow.preparer = groupValue;
      else if (groupByKey === 'reviewer') groupRow.reviewer = groupValue;
      result.push(groupRow);
    });
    return result;
  };

  const tableDataSource = useMemo(() => {
    if (!groupBy) return displayDataSource;
    return buildGroupedDataSource(displayDataSource, groupBy as GroupByKey);
  }, [displayDataSource, groupBy]);

  const handlePreferences = (
    action: PreferenceActions,
    preference: Preference
  ) => {
    if (currentUser) {
      return preferences(
        action,
        PreferenceSections.MAIN,
        preference,
        currentUser?.userUuid
      );
    }
    return false;
  };

  const handleOpenFilterSelection = () => {
    const drawerData: DrawerData = {
      title: t('submissions.select-filters-drawer-title'),
      subtitle: t('submissions.select-filters-subtitle'),
      type: DrawerType.FILTER_SELECTION,
      component: (
        <FilterSelectionDrawer
          availableFilters={PLANNER_AVAILABLE_FILTERS}
          selectedFilters={visibleFilters}
          onUpdate={(selectedFilters) => {
            setVisibleFilters(selectedFilters);
            if (currentUser) {
              handlePreferences(PreferenceActions.SET, {
                name: PLANNER_VISIBLE_FILTERS,
                value: selectedFilters,
              });
            }
          }}
          onClose={() => dispatch(turnDrawerOff())}
          handlePreferences={handlePreferences}
          currentUser={currentUser}
        />
      ),
    };
    dispatch(turnDrawerOn(drawerData));
  };

  const savePageSize = (page: number) => {
    setPageSizeSaved(page);
    if (currentUser) {
      handlePreferences(PreferenceActions.SET, {
        name: PLANNER_PAGE_SIZE,
        value: page,
      });
    }
  };

  const renderSortIcon = ({ sortOrder }: { sortOrder?: string }) => {
    if (sortOrder === 'ascend') {
      return <ArrowDownOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)' }} />;
    }
    return <ArrowUpOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)' }} />;
  };

  const renderOrgLink = (
    content: string,
    record: MappedReturn & { children?: MappedReturn[] }
  ) => {
    if (record.children) {
      return (
        <Typography.Text style={{ fontSize: '13px', fontWeight: 500 }}>
          {content || '—'}
        </Typography.Text>
      );
    }
    const to = getEntityOrganisationPath(
      record.organisationId,
      record.providerName
    );

    return (
      <Space size="small">
        {record.providerName && (
          <ProviderLogoNav
            providerName={record.providerName}
            shortCode={record.shortCode}
            organisationId={record.organisationId}
            style={{ height: '32px', display: 'block' }}
            onClick={(e) => e.stopPropagation()}
          >
            <ProviderIcon
              providerName={record.providerName}
              alt={record.providerName}
              width={25}
              style={{ paddingTop: '4px' }}
            />
          </ProviderLogoNav>
        )}
        <StyledTooltip
          key={record.organisationId}
          placement="top"
          title={t('main.click-to-view-details')}
        >
          <StrongTag>
            <Link to={to} onClick={clearOrganisationDetailsTabStorage}>
              <Typography.Text style={{ fontSize: '13px' }}>
                {content || '—'}
              </Typography.Text>
            </Link>
          </StrongTag>
        </StyledTooltip>
      </Space>
    );
  };

  const renderDueDate = (
    content: string | undefined,
    record: MappedReturn & { children?: MappedReturn[] }
  ) => {
    if (record.children) return content ?? '—';
    if (!content) return null;
    let dueDate: moment.Moment;
    if (content instanceof Date) {
      dueDate = moment(content.toISOString());
    } else if (moment.isMoment(content)) {
      dueDate = moment(content.valueOf());
    } else {
      dueDate = moment(content);
    }
    const currentDate = moment();
    return (
      <span
        className={
          dueDate.valueOf() < currentDate.valueOf() ? 'expired-due-date' : ''
        }
      >
        {dueDate.format('DD/MM/YYYY')}
      </span>
    );
  };

  const getFrequencyLabel = (returnFrequency: string | undefined) => {
    if (!returnFrequency) return '—';
    return getReturnFrequencyLabel(returnFrequency, t) || returnFrequency;
  };

  const getNextPeriodDateRange = (record: MappedReturn): string => {
    const fromDate = record.periodFromDate;
    const returnFrequency = record.returnFrequency;
    if (!fromDate || !returnFrequency) return '—';
    const result = calculateNextPeriod(fromDate, returnFrequency);
    return result && result[0] ? result[0] : '—';
  };

  const columns = [
    {
      title: t('common.entity'),
      dataIndex: 'organisation',
      key: 'organisation',
      render: renderOrgLink,
      width: '140px',
      sorter: (a: MappedReturn, b: MappedReturn) =>
        (a.organisation ?? '').localeCompare(b.organisation ?? ''),
      sortIcon: renderSortIcon,
    },
    {
      title: t('main.taxReturn'),
      dataIndex: 'mappingName',
      key: 'mappingName',
      render: (content: string) => (
        <Typography.Text
          style={{
            maxWidth: 200,
            fontSize: 13,
            fontWeight: 400,
            padding: '6px 8px',
          }}
          ellipsis={{ tooltip: true }}
        >
          {localizeReturnLabel(content || '—')}
        </Typography.Text>
      ),
      sorter: (a: MappedReturn, b: MappedReturn) =>
        (a.mappingName ?? '').localeCompare(b.mappingName ?? ''),
      sortIcon: renderSortIcon,
    },
    {
      title: t('planner.frequency'),
      dataIndex: 'returnFrequency',
      key: 'frequency',
      render: (
        _: unknown,
        record: MappedReturn & { children?: MappedReturn[] }
      ) =>
        record.children
          ? record.returnFrequency ?? '—'
          : getFrequencyLabel(record.returnFrequency),
      width: '120px',
      sorter: (a: MappedReturn, b: MappedReturn) =>
        getFrequencyLabel(a.returnFrequency).localeCompare(
          getFrequencyLabel(b.returnFrequency)
        ),
      sortIcon: renderSortIcon,
    },
    {
      title: t('main.period'),
      dataIndex: 'periodFromDate',
      key: 'period',
      render: (
        _: unknown,
        record: MappedReturn & { children?: MappedReturn[] }
      ) =>
        record.children
          ? record.periodFromDate ?? '—'
          : getNextPeriodDateRange(record),
      width: '180px',
      sorter: (a: MappedReturn, b: MappedReturn) =>
        getNextPeriodDateRange(a).localeCompare(getNextPeriodDateRange(b)),
      sortIcon: renderSortIcon,
    },
    {
      title: t('main.nextDueDate'),
      dataIndex: 'nextDueDate',
      key: 'nextDueDate',
      render: renderDueDate,
      defaultSortOrder: 'ascend' as const,
      sorter: (a: MappedReturn, b: MappedReturn) =>
        new Date(a.nextDueDate || 0).valueOf() -
        new Date(b.nextDueDate || 0).valueOf(),
      sortIcon: renderSortIcon,
    },
    {
      title: t('main.preparer'),
      dataIndex: 'preparer',
      key: 'preparer',
      sorter: (a: MappedReturn, b: MappedReturn) =>
        (
          a.preparer?.firstName ??
          a.dataLog?.preparerUser?.firstName ??
          ''
        ).localeCompare(
          b.preparer?.firstName ?? b.dataLog?.preparerUser?.firstName ?? ''
        ),
      sortIcon: renderSortIcon,
      render: (
        _: unknown,
        record: MappedReturn & { children?: MappedReturn[] }
      ) => {
        if (record.children) return record.preparer ?? '—';
        if (
          currentUser &&
          AccountType[currentUser.accountType] === AccountType.CLIENT
        )
          return null;
        const user = record.preparer ?? record.dataLog?.preparerUser;
        if (!user) return '—';
        return renderAvatar(
          user.firstName ?? '',
          user.lastName ?? '',
          AvatarSizes.DEFAULT,
          user.userUuid,
          user.profilePicture
        );
      },
      responsive: ['lg'] as const,
    },
    {
      title: t('main.reviewer'),
      dataIndex: 'reviewer',
      key: 'reviewer',
      sorter: (a: MappedReturn, b: MappedReturn) =>
        (
          a.reviewer?.firstName ??
          a.dataLog?.reviewerUser?.firstName ??
          ''
        ).localeCompare(
          b.reviewer?.firstName ?? b.dataLog?.reviewerUser?.firstName ?? ''
        ),
      sortIcon: renderSortIcon,
      render: (
        _: unknown,
        record: MappedReturn & { children?: MappedReturn[] }
      ) => {
        if (record.children) return record.reviewer ?? '—';
        if (
          currentUser &&
          AccountType[currentUser.accountType] === AccountType.CLIENT
        )
          return null;
        const user = record.reviewer ?? record.dataLog?.reviewerUser;
        if (!user) return '—';
        return renderAvatar(
          user.firstName ?? '',
          user.lastName ?? '',
          AvatarSizes.DEFAULT,
          user.userUuid,
          user.profilePicture
        );
      },
      responsive: ['lg'] as const,
    },
    {
      title: t('main.status'),
      dataIndex: 'status',
      key: 'status',
      width: '140px',
      render: (
        content: string,
        record: PlannerRow & { children?: PlannerRow[] }
      ) => {
        if (record.children) return '—';
        return <StatusGetter status={content} noDraft />;
      },
      sorter: (a: PlannerRow, b: PlannerRow) =>
        (a.status ?? '').localeCompare(b.status ?? ''),
      sortIcon: renderSortIcon,
    },
    {
      title: t('planner.planning'),
      key: 'planning',
      width: 280,
      render: (
        _: unknown,
        record: PlannerRow & { children?: PlannerRow[] }
      ) => {
        if (record.children) return '—';
        const planned = record.plannedDate
          ? moment(record.plannedDate).format('DD/MM')
          : '—';
        const p = record.priority ?? '—';
        const c = record.complexity ?? '—';
        const summary = `Planned: ${planned} · P: ${p} · C: ${c}`;
        return (
          <Space size="middle" wrap>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {summary}
            </Typography.Text>
            <Button
              type="link"
              size="small"
              style={{ padding: 0 }}
              onClick={() => setPlanningDetailRecord(record)}
            >
              {t('vat-checker.details')}
            </Button>
            <Button
              type="link"
              size="small"
              icon={<CalendarOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                Modal.info({
                  title: t('planner.add-to-calendar'),
                  content: t('planner.add-to-calendar-description'),
                });
              }}
            />
          </Space>
        );
      },
    },
  ];

  const emptyPageCallback = () => {
    if (hasEntities) {
      navigate(routes.organisations.all);
    }
  };

  const applyBulkOverride = (
    field: keyof PlannerRowExtension,
    value: string | null
  ) => {
    setPlannerOverrides((prev) => {
      const next = { ...prev };
      selectedRowKeys.forEach((k) => {
        const key = String(k);
        next[key] = { ...next[key], [field]: value };
      });
      return next;
    });
  };

  const PLANNER_MASS_UPDATE_FIELDS = [
    { value: 'priority', label: t('planner.priority') },
    { value: 'complexity', label: t('planner.complexity') },
    { value: 'plannedDate', label: t('planner.planned-date') },
    { value: 'preparerReviewer', label: t('planner.preparer-reviewer') },
  ] as const;

  const handleMassUpdateApply = () => {
    if (massUpdateField === 'preparerReviewer') {
      Modal.info({
        content: t('planner.preparer-reviewer-backend-hint'),
      });
      return;
    }
    if (massUpdateField === 'priority' || massUpdateField === 'complexity') {
      if (massUpdateValue != null && massUpdateValue !== '')
        applyBulkOverride(massUpdateField, massUpdateValue);
    } else if (massUpdateField === 'plannedDate') {
      if (massUpdateValue != null && massUpdateValue !== '')
        applyBulkOverride('plannedDate', massUpdateValue);
    }
    setMassUpdateField('');
    setMassUpdateValue(undefined);
  };

  const massUpdateTitleComponent = useMemo(() => {
    if (!massUpdateMode) return undefined;
    const totalItems = tableDataSource?.length ?? 0;
    const selectedCount = selectedRowKeys.length;
    const itemString =
      selectedCount === 0
        ? `${totalItems} ${t('organisation.items-on-page')}`
        : `${selectedCount} ${t('organisation.out-of')} ${totalItems} ${t(
            'organisation.records-selected'
          )}`;
    const filterStyle = { minWidth: 200, width: 200, height: 32 };
    return (
      <Space
        direction="horizontal"
        wrap
        size="middle"
        align="center"
        style={{ width: '100%', fontSize: 11, paddingBottom: 8 }}
      >
        <span style={{ color: '#5b6b73' }}>{itemString}</span>
        <FilterSelect
          placeholder={t('organisation.select-field-to-update')}
          value={massUpdateField || undefined}
          onChange={(val) => {
            setMassUpdateField(typeof val === 'string' ? val : '');
            setMassUpdateValue(undefined);
          }}
          options={PLANNER_MASS_UPDATE_FIELDS.map((f) => ({
            value: f.value,
            label: f.label,
          }))}
          style={filterStyle}
        />
        {massUpdateField === 'priority' && (
          <Select
            placeholder={t('planner.priority')}
            style={{ width: 120, height: 32 }}
            allowClear
            value={massUpdateValue ?? undefined}
            onChange={(v) => setMassUpdateValue(v ?? undefined)}
            options={PRIORITIES.map((p) => ({ value: p, label: p }))}
          />
        )}
        {massUpdateField === 'complexity' && (
          <Select
            placeholder={t('planner.complexity')}
            style={{ width: 120, height: 32 }}
            allowClear
            value={massUpdateValue ?? undefined}
            onChange={(v) => setMassUpdateValue(v ?? undefined)}
            options={COMPLEXITIES.map((c) => ({ value: c, label: c }))}
          />
        )}
        {massUpdateField === 'plannedDate' && (
          <DatePicker
            placeholder={t('planner.planned-date')}
            style={{ width: 140, height: 32 }}
            format="DD/MM/YYYY"
            value={
              massUpdateValue
                ? moment(massUpdateValue).isValid()
                  ? moment(massUpdateValue)
                  : null
                : null
            }
            onChange={(date) =>
              setMassUpdateValue(date ? date.format('YYYY-MM-DD') : undefined)
            }
          />
        )}
        {massUpdateField === 'preparerReviewer' && (
          <Button
            size="small"
            onClick={() =>
              Modal.info({
                content: t('planner.preparer-reviewer-backend-hint'),
              })
            }
          >
            {t('planner.select-users')}
          </Button>
        )}
        <ScopeButton
          type="dark-orange"
          size="small"
          fontSize="11px"
          lineheight="14px"
          height="24px"
          width="auto"
          style={{ paddingLeft: 10, paddingRight: 10 }}
          disabled={
            selectedCount === 0 ||
            !massUpdateField ||
            (massUpdateField !== 'preparerReviewer' &&
              (massUpdateValue == null || massUpdateValue === ''))
          }
          onClick={handleMassUpdateApply}
        >
          {t('organisation.apply-update')}
        </ScopeButton>
      </Space>
    );
  }, [
    massUpdateMode,
    selectedRowKeys,
    massUpdateField,
    massUpdateValue,
    tableDataSource?.length,
    t,
  ]);

  const rowSelection = useMemo(
    () =>
      massUpdateMode
        ? {
            selectedRowKeys,
            onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
            getCheckboxProps: (record: any) => ({
              disabled: !!record.children,
            }),
          }
        : undefined,
    [massUpdateMode, selectedRowKeys]
  );

  return (
    <>
      <Helmet>
        <title>{`${t('navigation.planner')} | ${process.env.PROJECT_NAME}`}</title>
        <meta
          name="description"
          content={t('planner.meta-description')}
        />
      </Helmet>
      <div
        style={{
          padding: '40px',
          paddingTop: '10px',
          paddingBottom: '40px',
          maxWidth: 1662,
          minWidth: 1200,
          margin: 'auto',
          width: '100%',
          overflowX: 'hidden',
          boxSizing: 'border-box',
        }}
      >
        <Row
          style={{
            marginTop: -5,
            marginBottom: 20,
            position: 'relative',
            width: '100%',
            maxWidth: '100%',
            overflowX: 'hidden',
          }}
        >
          <Typography.Title
            level={3}
            style={{ marginBottom: 0, marginRight: 24 }}
          >
            {t('navigation.planner')}
          </Typography.Title>
          <ScopeTabs
            activeKey={plannerViewType}
            onChange={(k) => setPlannerViewType(k as 'manager' | 'my')}
            items={
              canUseManagerView
                ? [
                    { key: 'manager', label: t('dashboard.manager-view') },
                    { key: 'my', label: t('dashboard.my-view') },
                  ]
                : [{ key: 'my', label: t('dashboard.my-view') }]
            }
            style={{ marginBottom: 0 }}
          />
        </Row>

        {loading ? (
          <>
            <div style={{ marginBottom: 16 }}>
              <Skeleton.Input active size="small" style={{ width: 200 }} />
            </div>
            <DashboardContainer className="non-selected-container">
              <Space direction="vertical" style={{ width: '100%', rowGap: 6 }}>
                <Skeleton.Avatar active size="large" shape="square" />
                <Skeleton.Input active size="small" style={{ width: '80px' }} />
                <Skeleton.Input active size="small" style={{ width: '40px' }} />
              </Space>
            </DashboardContainer>
          </>
        ) : !mappedReturns || mappedReturns.length === 0 ? (
          <EmptyPage
            titleText={
              hasEntities ? t('organisation.empty-tax-returns-title') : t('organisation.empty-entities-title')
            }
            actionText={
              hasEntities
                ? t('submissions.empty-returns-action-proceed')
                : !currentUser?.canManageOrgs
                ? t('submissions.empty-entities-contact-owner')
                : t('organisation.empty-entities-action')
            }
            buttonText={hasEntities ? t('common.continue') : t('organisation.empty-entities-button')}
            actionCallback={emptyPageCallback}
            disableButton={!currentUser?.canManageOrgs}
            showIcon={!hasEntities}
          />
        ) : (
          <>
            <div style={{ marginBottom: '16px' }}>
              <ScopeTableFilters
                filters={PLANNER_AVAILABLE_FILTERS}
                visibleFilters={visibleFilters}
                value={sharedFiltersState}
                onChange={setSharedFiltersState}
                originalDataSource={mappedReturns}
                tableDataSource={tableDataSource}
                onOpenFilterSelection={handleOpenFilterSelection}
                filtersCollapsedByDefault
                filterExtraContent={
                  <Space style={{ marginBottom: 16, flexWrap: 'wrap' }}>
                    <Typography
                      style={{
                        fontSize: '12px',
                        fontWeight: 400,
                        lineHeight: '12px',
                        color: 'var(--color-text-dark, #042a2a)',
                        minWidth: 35,
                      }}
                    >
                      {t('planner.group-by')}
                    </Typography>
                    <FilterSelect
                      placeholder={t('main.vat-checker-stat-flags-none')}
                      style={{ minWidth: '150px' }}
                      height="45px"
                      value={groupBy || undefined}
                      onChange={(v) => setGroupBy((v as string) ?? '')}
                      allowClear
                      options={GROUP_BY_OPTIONS}
                    />
                  </Space>
                }
                embedded={false}
              />
              <ScopeTable
                renderRightComponent={
                  <IconButton
                    type="default"
                    icon={<CheckSquareOutlined style={{ fontSize: 14 }} />}
                    style={{
                      height: '32px',
                      width: 'auto',
                      backgroundColor: massUpdateMode ? 'var(--color-layout-bg, #e9f6f7)' : 'var(--color-surface-raised, #FFFFFF)',
                      border: `2px solid ${
                        massUpdateMode ? '#41aaaf' : 'var(--color-border, #dbeae3)'
                      }`,
                      borderRadius: '10px',
                      boxShadow: '0px 0px 10px 0px #0000000d',
                      color: 'var(--color-text-dark, #042a2a)',
                      fontSize: '12px',
                      fontWeight: 600,
                      lineHeight: '12px',
                      padding: '0 12px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    onClick={() => {
                      setMassUpdateMode((prev) => {
                        if (prev) {
                          setSelectedRowKeys([]);
                          setMassUpdateField('');
                          setMassUpdateValue(undefined);
                          return false;
                        }
                        return true;
                      });
                    }}
                  >
                    {massUpdateMode
                      ? t('organisation.mass-update-cancel')
                      : t('organisation.mass-update')}
                  </IconButton>
                }
                titleComponent={massUpdateTitleComponent}
                originalDataSource={mappedReturns}
                tableDataSource={tableDataSource}
                rowSelection={rowSelection}
                rowKey={(record: any) =>
                  record.plannerRowKey ?? record.key ?? getPlannerRowKey(record)
                }
                columns={columns}
                pagination
                rowsPerPage={pageSizeSaved || 20}
                enableColumnsCustomization={false}
                haveBorder
                showSizeChanger
                getNewPageSize={savePageSize}
                rowHeight={50}
              />
            </div>
          </>
        )}
      </div>
      <Modal
        title={t('planner.planning-details')}
        open={!!planningDetailRecord}
        onCancel={() => setPlanningDetailRecord(null)}
        footer={
          <Button type="primary" onClick={() => setPlanningDetailRecord(null)}>
            {t('common.close')}
          </Button>
        }
        width={420}
      >
        {planningDetailRecord && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Typography.Text type="secondary">
                {t('planner.planned-date')}
              </Typography.Text>
              <br />
              {planningDetailRecord.plannedDate
                ? formatDisplayDate(
                    planningDetailRecord.plannedDate,
                    'DD/MM/YYYY',
                    i18n.language
                  )
                : '—'}
            </div>
            <div>
              <Typography.Text type="secondary">{t('planner.priority')}</Typography.Text>
              <br />
              {planningDetailRecord.priority ?? '—'}
            </div>
            <div>
              <Typography.Text type="secondary">{t('planner.complexity')}</Typography.Text>
              <br />
              {planningDetailRecord.complexity ?? '—'}
            </div>
            <div>
              <Typography.Text type="secondary">{t('planner.comments')}</Typography.Text>
              <br />
              {planningDetailRecord.comments ?? '—'}
            </div>
            <div>
              <Typography.Text type="secondary">
                {t('planner.documents-requested')}
              </Typography.Text>
              <br />
              {planningDetailRecord.documentsRequestedDate
                ? formatDisplayDate(
                    planningDetailRecord.documentsRequestedDate,
                    'DD/MM/YYYY',
                    i18n.language
                  )
                : '—'}
            </div>
            <div>
              <Typography.Text type="secondary">
                {t('planner.documents-received')}
              </Typography.Text>
              <br />
              {planningDetailRecord.documentsReceivedDate
                ? formatDisplayDate(
                    planningDetailRecord.documentsReceivedDate,
                    'DD/MM/YYYY',
                    i18n.language
                  )
                : '—'}
            </div>
            <Button
              type="link"
              size="small"
              icon={<CalendarOutlined />}
              onClick={() => {
                setPlanningDetailRecord(null);
                Modal.info({
                  title: t('planner.add-to-calendar'),
                  content: t('planner.add-to-calendar-description'),
                });
              }}
            >
              {t('planner.add-to-calendar')}
            </Button>
          </Space>
        )}
      </Modal>
    </>
  );
};
